"use client";

import { useState } from "react";
import { ShoppingCart, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/store/cart.store";
import { cartKey } from "@/lib/utils";
import { ProductDetail, ProductVariant } from "@/types";

interface AddToCartButtonProps {
  product: ProductDetail;
  selectedVariant?: ProductVariant;
  quantity?: number;
  onAdd?: (btnRef: HTMLButtonElement) => void;
}

export function AddToCartButton({ product, selectedVariant, quantity = 1, onAdd }: AddToCartButtonProps) {
  const addItem = useCartStore((s) => s.addItem);
  const items = useCartStore((s) => s.items);
  const [added, setAdded] = useState(false);

  const effectivePrice = selectedVariant?.price ?? product.price;
  const effectiveStock = selectedVariant?.stock ?? product.stock;

  // Sepette zaten kaç adet var?
  const cartQty =
    items.find(
      (i) => cartKey(i.productId, i.variantId) === cartKey(product.id, selectedVariant?.id)
    )?.quantity ?? 0;

  // Kaç adet daha eklenebilir?
  const remaining = product.trackStock ? Math.max(0, effectiveStock - cartQty) : Infinity;

  const isOutOfStock = product.trackStock && (effectiveStock <= 0 || remaining <= 0);
  const isOverStock = product.trackStock && remaining > 0 && quantity > remaining;
  const needsVariant = product.variants.length > 0 && !selectedVariant;

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isOutOfStock || isOverStock || needsVariant) return;

    addItem({
      productId: product.id,
      variantId: selectedVariant?.id,
      name: product.name,
      variantName: selectedVariant?.name,
      price: effectivePrice,
      dealerPrice: product.dealerPrice ?? null,
      imageUrl: product.images.find((i) => i.isCover)?.url ?? product.coverImageUrl,
      quantity,
      slug: product.slug,
      stock: effectiveStock,
      trackStock: product.trackStock,
    });

    onAdd?.(e.currentTarget);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (needsVariant) {
    return (
      <Button className="h-full w-full whitespace-nowrap px-3 text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs sm:tracking-widest" size="lg" disabled>
        <ShoppingCart className="h-4 w-4 shrink-0" />
        Seçenek Seçin
      </Button>
    );
  }

  if (isOutOfStock) {
    return (
      <Button className="h-full w-full whitespace-nowrap px-3 text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs sm:tracking-widest" size="lg" disabled variant="outline">
        Stokta Yok
      </Button>
    );
  }

  if (isOverStock) {
    return (
      <Button className="h-full w-full whitespace-nowrap px-3 text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs sm:tracking-widest" size="lg" disabled variant="outline">
        Yetersiz Stok (Max {remaining})
      </Button>
    );
  }

  return (
    <Button
      className="h-full w-full whitespace-nowrap px-3 text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs sm:tracking-widest"
      size="lg"
      onClick={handleAdd}
      variant={added ? "outline" : "primary"}
    >
      {added ? (
        <>
          <Check className="h-4 w-4 shrink-0" />
          Sepete Eklendi
        </>
      ) : (
        <>
          <ShoppingCart className="h-4 w-4 shrink-0" />
          Sepete Ekle
        </>
      )}
    </Button>
  );
}
